/**
 * LoginModal Component
 * Compact two-panel login dialog. Left: credentials form. Right: product
 * visual consistent with the public landing page (light theme, token-driven).
 */

import { useState } from 'react';
import { config } from '@/services/config';
import { useNavigate } from 'react-router-dom';
import { useTenantAuthStore } from '@/store/tenantAuthStore';
import { useLandingPage } from '@/hooks/useLandingPage';
import {
  Dialog,
  DialogContent,
} from '@/components/shadcn/dialog';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import {
  AlertCircle,
  BadgeCheck,
  Building2,
  Database,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Lock,
  Server,
  ShieldCheck,
  User,
  X,
} from 'lucide-react';

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginModal({ open, onOpenChange }: LoginModalProps) {
  const [tenantSlug, setTenantSlug] = useState('credex-tech');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { loginWithTenant, isLoading, error, clearError } = useTenantAuthStore();
  const navigate = useNavigate();
  const landingPage = useLandingPage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      // loginWithTenant now returns the user data directly
      const loggedInUser = await loginWithTenant(email, password, tenantSlug);
      onOpenChange(false);

      // Use the returned user data instead of the hook state
      // This prevents race conditions with state updates
      const targetPage = loggedInUser?.tenant?.settings?.landing_page || landingPage;

      // Only navigate if we're not already on the target page
      if (window.location.pathname !== targetPage) {
        navigate(targetPage);
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
      setTenantSlug('credex-tech');
      setEmail('');
      setPassword('');
      setShowPassword(false);
      clearError();
    }
  };

  const promoFeatures = [
    { icon: BadgeCheck, label: 'Every answer shows its sources' },
    { icon: Server, label: "Runs inside your company's environment" },
    { icon: ShieldCheck, label: 'Safety checks on every question and answer' },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="w-[95vw] max-w-4xl gap-0 overflow-hidden rounded-xl border-card-border bg-background p-0 shadow-2xl sm:w-[90vw] md:w-[88vw] lg:w-full"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-3 top-3 z-50 rounded-full bg-background/80 p-1.5 text-slate-400 backdrop-blur-sm transition-colors hover:bg-slate-50 hover:text-foreground"
          disabled={isLoading}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Two-Panel Layout */}
        <div className="flex max-h-[80vh] flex-col overflow-hidden md:flex-row">
          {/* Left Panel — Login Form */}
          <div className="relative flex w-full flex-col justify-center bg-background p-8 md:w-1/2 md:p-8 lg:p-8">
            <div className="mx-auto w-full max-w-md">
              {/* Header */}
              <div className="mb-7 text-center md:text-left">
                <div className="mb-6 flex items-center justify-center gap-3 md:justify-start">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md border border-primary/20 bg-primary/10 text-primary">
                    <Database className="h-4 w-4" />
                  </div>
                  <span className="text-base font-semibold tracking-tight text-foreground">
                    {config.app.name}
                  </span>
                </div>

                <h2 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
                  Welcome back
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  Sign in to your workspace to continue.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Tenant Slug Field */}
                <div className="flex flex-col gap-1.5">
                  <Label
                    htmlFor="tenantSlug"
                    className="text-tiny font-medium uppercase tracking-wide text-slate-600"
                  >
                    Organization ID
                  </Label>
                  <div className="group relative">
                    <Input
                      id="tenantSlug"
                      type="text"
                      placeholder="e.g., credex-tech"
                      value={tenantSlug}
                      onChange={(e) => setTenantSlug(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-10 rounded-md border-slate-300 bg-background pl-9 text-caption pr-3 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 transition-colors group-focus-within:text-primary">
                      <Building2 className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                {/* Email Field */}
                <div className="flex flex-col gap-1.5">
                  <Label
                    htmlFor="email"
                    className="text-tiny font-medium uppercase tracking-wide text-slate-600"
                  >
                    Email
                  </Label>
                  <div className="group relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-10 rounded-md border-slate-300 bg-background pl-9 text-caption pr-3 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 transition-colors group-focus-within:text-primary">
                      <User className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                {/* Password Field */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="password"
                      className="text-tiny font-medium uppercase tracking-wide text-slate-600"
                    >
                      Password
                    </Label>
                    <button
                      type="button"
                      className="text-tiny font-medium text-primary transition-colors hover:underline"
                      onClick={() => {
                        onOpenChange(false);
                        navigate('/forgot-password');
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="group relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-10 rounded-md border-slate-300 bg-background pl-9 text-caption pr-10 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 transition-colors group-focus-within:text-primary">
                      <Lock className="h-4 w-4" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-600 transition-colors hover:text-foreground"
                      disabled={isLoading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive" className="py-2">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="mt-2 h-10 w-full text-white font-semibold shadow-sm transition-all hover:shadow-primary focus:ring-4 focus:ring-primary/30"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in…
                    </span>
                  ) : (
                    'Sign in'
                  )}
                </Button>

                {/* Footer Note */}
                <p className="mt-1 text-center text-xs text-slate-500">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="font-medium text-primary transition-colors hover:underline"
                    onClick={() => {
                      onOpenChange(false);
                      navigate('/contact');
                    }}
                  >
                    Contact sales
                  </button>
                </p>
              </form>
            </div>
          </div>

          {/* Right Panel — Product Visual (matches landing hero aesthetic) */}
          <div className="relative hidden flex-col justify-center overflow-hidden border-l border-border bg-gradient-hero-glow md:flex md:w-1/2">
            <div className="absolute inset-0 bg-pattern-grid-hero opacity-70"></div>

            <div className="relative z-10 flex h-full flex-col justify-center gap-8 p-10 lg:p-12">
              {/* Status Badge */}
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1 text-tiny font-semibold uppercase tracking-wide text-slate-500 shadow-sm backdrop-blur-sm">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-text opacity-75"></span>
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success-text"></span>
                </span>
                Assistant ready
              </div>

              {/* Headline */}
              <div>
                <h3 className="text-2xl font-semibold leading-tight tracking-tight text-foreground text-balance">
                  Your documents, ready to answer.
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-500">
                  Ask in plain language and get answers backed by your company's own documents —
                  sources included.
                </p>
              </div>

              {/* Mini answer card (echoes the hero scene) */}
              <div className="max-w-xs rounded-xl border border-card-border bg-background/95 p-3 shadow-lg backdrop-blur-md">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-tiny font-semibold uppercase tracking-wider text-slate-400">
                      Answer
                    </span>
                    <span className="text-xs font-semibold text-foreground">
                      With sources attached
                    </span>
                  </div>
                </div>
                <div className="mt-2 rounded-md border border-border bg-slate-50 p-2">
                  <p className="text-tiny leading-relaxed text-slate-500">
                    Full-time employees get <strong>24 days</strong> of annual leave
                    <span className="mx-0.5 inline-flex items-baseline rounded-sm bg-primary/10 px-1 text-tiny font-semibold text-primary">
                      1
                    </span>
                    .
                  </p>
                  <div className="mt-1.5 flex items-center gap-1.5 border-t border-border pt-1.5 text-tiny text-slate-400">
                    <span className="rounded-sm bg-primary/10 px-1 font-semibold text-primary">1</span>
                    <span className="truncate">Leave_Policy.pdf</span>
                    <span className="ml-auto shrink-0">p. 3</span>
                  </div>
                </div>
              </div>

              {/* Feature list */}
              <ul className="flex flex-col gap-3 border-t border-border/60 pt-6">
                {promoFeatures.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <li
                      key={feature.label}
                      className="flex items-center gap-2.5 text-xs font-medium text-slate-500"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-primary" />
                      <span>{feature.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
